import _ = require("underscore");
import EventEmitter = require('wolfy87-eventemitter');
import Crypto = require("crypto-js");
import {request, timestamp} from "./model/utils";

interface ApiOptions {
    projectId: string,
    questionaryId: string,
    key: string,
    baseUrl: string,
    respondentId?: string,
    questionaryVersion?: number,
}

interface RequestOptions {
    url?: string,
    method?: string,
    dataType?: "text"|string,
    background?: boolean,
    data?: any,
    headers?: any,
}

export default class Api extends EventEmitter {

	projectId: string;
	questionaryId: string;
	responentId?: string;
	key: string;
	baseUrl: string;
    questionaryVersion?: number;

	constructor(options: ApiOptions) {
		super();
		this.projectId = options.projectId;
		this.questionaryId = options.questionaryId;
		this.responentId = options.respondentId;
		this.key = options.key;
		this.baseUrl = options.baseUrl || '/ui/v1';
        this.questionaryVersion = options.questionaryVersion;
	}

	getSecurityHeaders(url: string, messageBody: string) {
		const t = timestamp();
		const hmac = Crypto.SHA1(url + messageBody + t + this.key) + '';
		return {
			seTimestamp: t,
			seHmac: hmac
		};
	}

	securedRequest(options: RequestOptions, failSilently: boolean = false): Promise<any> {
		const r = request(_.extend({}, options, {
			config: (xhr: JQueryXHR, options: RequestOptions) => {
				const messageBody = options.data;
				const url = <string>options.url;
				const headers = _.extend({}, options.headers, this.getSecurityHeaders(url, messageBody));
				if( this.questionaryId ) {
					headers.seQuestionaryId = this.questionaryId;
				}
				_.each(headers, (value: string, key: string) => xhr.setRequestHeader(key, value));
			}
		}));

		if(!failSilently){
			r.then(null, () => this.trigger('requestError', [{
					method: options.method,
					url: options.url,
					body: options.data
				}])
			);
		}

		return r;
	}

	getProjectUrl () {
		return this.baseUrl + '/projects/' + encodeURI(this.projectId);
	}
	getQuestionaryUrl () {
		return this.baseUrl + '/questionaries/' + encodeURI(this.questionaryId);
	}

	getFileUrl (filename: string) {
		if (filename && ['', 'http:', 'https:'].includes(filename.split('//')[0])) {
			return filename;
		}
        const query = this.questionaryVersion ? `?v=${this.questionaryVersion}` : ''; // invalids cache with new survey version
		return this.getProjectUrl() + '/questionary/files/' + filename + '/' + query;
	}

	getDefinition() {
		return this.securedRequest({
            url: `${this.getProjectUrl()}/questionary/definition?v=${this.questionaryVersion}`,
            dataType: 'text'
		});
	}
    getDictionary() {
        return this.securedRequest({url: this.getProjectUrl() + '/questionary/dictionary', dataType: 'text'});
    }
	getCustomizations() { }

	//business
	saveStatus (data: any) {
		return this.securedRequest({method: 'POST', background: true, data: data, url: this.getQuestionaryUrl() + '/status' });
	}
	getAnswers (): Promise<any> {
		return this.securedRequest({background: true, url: this.getQuestionaryUrl() + '/answers' });
	}
	saveAnswers (data: any) {
        this.trySaveToLocalStorage(data, 'answers');
        return this.securedRequest({method: 'POST', background: true, data: data, url: this.getQuestionaryUrl() + '/answers' });
	}
	getAttributes () {
		return this.securedRequest({background: true, url: this.getQuestionaryUrl() + '/attributes' });
	}
	getQuotas () {
		return this.securedRequest({background: true, url: this.getProjectUrl() + '/quotas' });
	}
	getIpAddress(): Promise<string> {
        return this.securedRequest({background: true, url: this.baseUrl + '/ip', dataType: "text"});
    }

	// service data
	getServiceData () {
		return this.securedRequest({background: true, url: this.getQuestionaryUrl() + '/service-data' });
	}
	saveServiceData (data: any) {
		return this.securedRequest({method: 'POST', background: true, data: data, url: this.getQuestionaryUrl() + '/service-data' });
	}

	// save multiple types of data in one request
	public saveData(data: {answerData?: any, serviceData?: any, events?: any}): Promise<any> {
	    if (data.answerData) {
	        this.trySaveToLocalStorage(data.answerData, 'answers');
        }

        return this.securedRequest({
            method: 'POST',
            background: true,
            data: data,
            url: this.getQuestionaryUrl() + '/data',
        })
    }

	//events
	saveEvents (events: any) {
		return this.securedRequest({
			method: 'POST',
			background: true,
			data: events,
			url: this.getQuestionaryUrl() + '/events',
			headers: {
				seClientTime: timestamp()
			}
		}, true);
	}

	uploadFiles (
	    data: FormData
    ): Promise<any> {
        return this.securedRequest({
            url: this.baseUrl + '/questionaries/' + this.questionaryId + '/files',
            data: data,
            method: 'POST',
            dataType: 'text',
        });
    }

    private trySaveToLocalStorage(data: any, key: string) {
        try {
            if (typeof Storage !== void(0)) {
                const id = [this.questionaryId, this.projectId, key, this.key].join('~');
                localStorage.setItem(id, JSON.stringify({
                    projectId: this.projectId,
                    timestamp: timestamp(),
                    questionaryId: this.questionaryId,
                    questionaryUrl: this.getQuestionaryUrl() + '/'+key,
                    method: 'POST',
                    data: data,
                }));
            }
        } catch (e) {
            // Do nothing. This is not critical feature yet
        }
    }
}
